/**
 * 모바일 환경 감지 훅
 * - 화면 너비 기반 반응형 감지
 * - 터치 기기 여부 감지
 */

import { useState, useEffect } from 'react';

interface MobileState {
  /** 모바일 화면 여부 (< 768px) */
  isMobile: boolean;
  /** 태블릿 화면 여부 (768px ~ 1024px) */
  isTablet: boolean;
  /** 데스크톱 화면 여부 (>= 1024px) */
  isDesktop: boolean;
  /** 터치 기기 여부 */
  isTouchDevice: boolean;
  /** 현재 화면 너비 */
  screenWidth: number;
}

const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

export function useMobileDetect(): MobileState {
  const [state, setState] = useState<MobileState>(() => {
    if (typeof window === 'undefined') {
      return {
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        isTouchDevice: false,
        screenWidth: 1024,
      };
    }

    const width = window.innerWidth;
    return {
      isMobile: width < MOBILE_BREAKPOINT,
      isTablet: width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT,
      isDesktop: width >= TABLET_BREAKPOINT,
      isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      screenWidth: width,
    };
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      const width = window.innerWidth;
      setState({
        isMobile: width < MOBILE_BREAKPOINT,
        isTablet: width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT,
        isDesktop: width >= TABLET_BREAKPOINT,
        isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
        screenWidth: width,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return state;
}

/**
 * 간단한 모바일 여부만 반환하는 훅
 */
export function useIsMobile(): boolean {
  const { isMobile } = useMobileDetect();
  return isMobile;
}
